import { Alert, Box, Button, CircularProgress, Grid, TextField, Typography, useTheme } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useNotification } from '../../../context/NotificationContext';
import { useTranslation } from '../../../hooks/useTranslation';
import { Platform } from '../../../services/integration.types';
import { QUERY_KEYS } from '../../../utils/queryKeys';
import { useIntegrationMutations } from '../hooks/useIntegrationMutations';

interface IntegrationBlockchainFormComponentProps {
    platform: Platform;
    onJobCreated: () => void;
}

const IntegrationBlockchainFormComponent: React.FC<IntegrationBlockchainFormComponentProps> = ({
    platform,
    onJobCreated,
}) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { showNotification } = useNotification();
    const theme = useTheme();
    const [formData, setFormData] = useState({
        wallet_address: '',
    });
    const [showJobWarning, setShowJobWarning] = useState(false);
    const [isQueryInvalidating, setIsQueryInvalidating] = useState(false);
    const { createWalletIntegration, isWalletCreating } = useIntegrationMutations();

    // Combined loading state to check if any operation is in progress
    const isLoading = isWalletCreating || isQueryInvalidating;

    const handleSubmit = async () => {
        setShowJobWarning(false);
        createWalletIntegration(
            {
                platform_type: platform.platform_type,
                platform_name: platform.name,
                wallet_address: formData.wallet_address,
            },
            {
                onSuccess: async () => {
                    setIsQueryInvalidating(true);
                    await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.INTEGRATIONS] });
                    showNotification(
                        t('common.integration.syncStarted', { platform: platform.display_name }),
                        'success'
                    );
                    setIsQueryInvalidating(false);
                    // Wait briefly to let user see the success message before navigating away
                    setTimeout(() => {
                        onJobCreated();
                    }, 1500);
                },
                onError: (error) => {
                    // Backend normalizes responses, so errors here are real failures
                    console.error('Wallet integration error:', error);
                    const errorMessage = error?.response?.data?.error ||
                                       error?.response?.data?.message ||
                                       error?.message ||
                                       t('integrations.blockchain.warning.invalidWallet');
                    showNotification(errorMessage, 'error');
                    setShowJobWarning(true);
                    // Don't call onJobCreated() - integration was not created
                },
            }
        );
    };

    if (!platform)
        return (
            <Typography variant="h6" gutterBottom>
                {t('common.integration.selectPlatform')}
            </Typography>
        );

    return (
        <>
            <Grid container spacing={3} sx={{ flexDirection: { xs: 'column', md: 'row' } }}>
                <Grid item xs={12} md={6}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            mt: 2,
                        }}
                    >
                        <TextField
                            sx={{ mb: 2 }}
                            label={t('integrations.blockchain.walletAddress')}
                            value={formData.wallet_address}
                            onChange={(e) => setFormData({ ...formData, wallet_address: e.target.value })}
                        />
                        <Button
                            variant="contained"
                            fullWidth
                            onClick={handleSubmit}
                            disabled={!formData.wallet_address || isLoading}
                            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : undefined}
                        >
                            {isLoading ? t('common.integration.connecting') : t('common.integration.connect')}
                        </Button>
                        {showJobWarning && (
                            <Alert severity="warning">{t('integrations.blockchain.warning.checkWallet')}</Alert>
                        )}
                    </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1.5,
                            p: 2,
                            height: '100%',
                            bgcolor:
                                theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.03)',
                            borderRadius: 1,
                        }}
                    >
                        <Typography variant="body2">{t('integrations.blockchain.enterPublicWallet')}</Typography>
                        <Box
                            component="ul"
                            sx={{
                                pl: 2,
                                m: 0,
                                mb: 1,
                            }}
                        >
                            <Typography component="li" variant="body2">
                                {t('integrations.blockchain.tokensAndNFTs')}
                            </Typography>
                            <Typography component="li" variant="body2">
                                {t('integrations.blockchain.transactionHistory')}
                            </Typography>
                        </Box>
                    </Box>
                </Grid>
            </Grid>
        </>
    );
};

export default IntegrationBlockchainFormComponent;
